import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Camera, CheckCircle2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PlateChip } from '../components/PlateChip';
import { api, errMsg } from '../lib/api';
import { compressImage } from '../lib/image';
import { FUEL_LABELS } from '../lib/format';

export default function IntakePage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [plate, setPlate] = useState('');
  const [lookup, setLookup] = useState(null); // null=idle, {found, vehicle, customer} or {found:false}
  const [looking, setLooking] = useState(false);
  const [form, setForm] = useState({
    complaint: '', odometer_in: '', fuel_level: '', condition_notes: '',
    customer_name: '', customer_mobile: '', make: '', model: '', year: '', color: '',
  });
  const [photos, setPhotos] = useState([]); // local {file, url}
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const doLookup = async (value) => {
    const p = (value || plate).trim();
    if (p.length < 2) { setLookup(null); return; }
    setLooking(true);
    try {
      const { data } = await api.get('/vehicles/lookup', { params: { plate: p } });
      setLookup(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLooking(false);
    }
  };

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (photos.length + files.length > 8) {
      toast.error('Maximum 8 condition photos per job.');
      return;
    }
    setPhotos([...photos, ...files.map((f) => ({ file: f, url: URL.createObjectURL(f) }))]);
  };

  const removePhoto = (idx) => {
    URL.revokeObjectURL(photos[idx].url);
    setPhotos(photos.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        plate: plate.trim().toUpperCase(),
        complaint: form.complaint,
        odometer_in: form.odometer_in ? parseInt(form.odometer_in, 10) : null,
        fuel_level: form.fuel_level || null,
        condition_notes: form.condition_notes || null,
      };
      if (!lookup?.found) {
        payload.customer_name = form.customer_name;
        payload.customer_mobile = form.customer_mobile;
        payload.make = form.make;
        payload.model = form.model || null;
        payload.year = form.year ? parseInt(form.year, 10) : null;
        payload.color = form.color || null;
      }
      const { data } = await api.post('/jobs', payload);
      for (const p of photos) {
        try {
          const compressed = await compressImage(p.file);
          const fd = new FormData();
          fd.append('file', compressed);
          fd.append('kind', 'condition');
          await api.post(`/jobs/${data.job.id}/photos`, fd);
        } catch {
          toast.error('One photo failed to upload — you can add it from the job page.');
        }
      }
      toast.success(data.message);
      navigate(`/jobs/${data.job.id}`);
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  const isReturning = lookup?.found;
  const showNewFields = lookup && !lookup.found;

  return (
    <div className="max-w-xl mx-auto space-y-4">
      <h1 className="text-xl font-semibold font-display">Receive a vehicle</h1>
      <form onSubmit={submit} className="space-y-4">
        <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
          <CardContent className="pt-5 space-y-3">
            <label className="text-sm font-medium">Plate number</label>
            <Input
              data-testid="intake-plate-input"
              required
              placeholder="e.g. NBC 1234"
              value={plate}
              onChange={(e) => setPlate(e.target.value.toUpperCase())}
              onBlur={() => doLookup()}
              className="h-12 rounded-xl font-plate text-base tracking-wider uppercase"
            />
            {looking && <p className="text-xs text-[hsl(var(--muted-foreground))]">Checking plate…</p>}
            {isReturning && (
              <div data-testid="intake-returning-summary" className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />
                <div>
                  <div className="font-medium">{lookup.customer.name} · {lookup.customer.mobile}</div>
                  <div className="text-[hsl(var(--muted-foreground))]">
                    {lookup.vehicle.year} {lookup.vehicle.make} {lookup.vehicle.model} · <PlateChip plate={lookup.vehicle.plate} />
                  </div>
                </div>
              </div>
            )}
            {showNewFields && (
              <div data-testid="intake-new-customer-fields" className="space-y-3 pt-1">
                <p className="text-xs text-[hsl(var(--muted-foreground))]">New vehicle — add the customer and vehicle details.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input data-testid="intake-customer-name-input" required placeholder="Customer name" value={form.customer_name} onChange={set('customer_name')} className="h-11 rounded-xl" />
                  <Input data-testid="intake-customer-mobile-input" required placeholder="Mobile (09171234567)" value={form.customer_mobile} onChange={set('customer_mobile')} className="h-11 rounded-xl" />
                  <Input data-testid="intake-make-input" required placeholder="Make (e.g. Toyota)" value={form.make} onChange={set('make')} className="h-11 rounded-xl" />
                  <Input data-testid="intake-model-input" placeholder="Model (e.g. Vios)" value={form.model} onChange={set('model')} className="h-11 rounded-xl" />
                  <Input data-testid="intake-year-input" type="number" min="1950" max="2035" placeholder="Year" value={form.year} onChange={set('year')} className="h-11 rounded-xl" />
                  <Input data-testid="intake-color-input" placeholder="Color" value={form.color} onChange={set('color')} className="h-11 rounded-xl" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
          <CardContent className="pt-5 space-y-3">
            <label className="text-sm font-medium">Complaint</label>
            <Textarea
              data-testid="intake-complaint-input"
              required
              minLength={3}
              placeholder='e.g. "Grinding noise from front when braking"'
              value={form.complaint}
              onChange={set('complaint')}
              className="rounded-xl min-h-[88px]"
            />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Odometer (km)</label>
                <Input data-testid="intake-odometer-input" type="number" min="0" placeholder="e.g. 45210" value={form.odometer_in} onChange={set('odometer_in')} className="h-11 rounded-xl" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Fuel level</label>
                <Select value={form.fuel_level} onValueChange={(v) => setForm({ ...form, fuel_level: v })}>
                  <SelectTrigger data-testid="intake-fuel-select" className="h-11 rounded-xl">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(FUEL_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="text-sm font-medium">Condition notes</label>
            <Textarea data-testid="intake-condition-notes-input" placeholder="Scratches, dents, items left in the vehicle…" value={form.condition_notes} onChange={set('condition_notes')} className="rounded-xl" />
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
          <CardContent className="pt-5 space-y-3">
            <label className="text-sm font-medium">Condition photos <span className="text-[hsl(var(--muted-foreground))] font-normal">(up to 8)</span></label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-[hsl(var(--border))]">
                  <img src={p.url} alt="condition" className="h-full w-full object-cover" />
                  <button type="button" data-testid="intake-photo-remove-button" aria-label="Remove photo" onClick={() => removePhoto(i)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {photos.length < 8 && (
                <button type="button" data-testid="intake-photo-add-button" onClick={() => fileRef.current?.click()} className="aspect-square rounded-xl border border-dashed border-[hsl(var(--border))] flex flex-col items-center justify-center gap-1 text-xs text-[hsl(var(--muted-foreground))] hover:bg-black/5 transition-colors">
                  <Camera className="h-5 w-5" /> Add photo
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple hidden onChange={addPhotos} />
          </CardContent>
        </Card>

        <div className="sticky bottom-3">
          <Button data-testid="intake-submit-button" type="submit" disabled={busy || looking} className="w-full h-12 rounded-xl text-base shadow-[var(--shadow-card)]">
            {busy ? 'Creating job order…' : 'Create job order'}
          </Button>
        </div>
      </form>
    </div>
  );
}
